import { useState } from 'react';
import type { Confidence, Scores, Vote } from '@mas-flow/types';
import { LEGACY_VOTER_ID } from '@mas-flow/domain';

type Dimension = keyof Scores | 'confidence';

function valueFor(v: Vote, dim: Dimension): number | Confidence | null {
  if (dim === 'confidence') return v.confidence;
  return v.scores[dim];
}

function renderValue(val: number | Confidence | null): string {
  if (val == null) return '—';
  return String(val);
}

export interface VoteDistributionProps {
  dimension: Dimension;
  votes: readonly Vote[];
  canSeeNames: boolean;
}

export function VoteDistribution({ dimension, votes, canSeeNames }: VoteDistributionProps) {
  const [open, setOpen] = useState(false);
  const present = votes.filter((v) => valueFor(v, dimension) != null);
  if (present.length === 0) {
    return <p className="text-xs italic text-slate-400">No votes yet</p>;
  }

  const sorted = [...present].sort((a, b) => {
    const va = valueFor(a, dimension);
    const vb = valueFor(b, dimension);
    if (typeof va === 'number' && typeof vb === 'number') return va - vb;
    return String(va).localeCompare(String(vb));
  });

  const summary = sorted.map((v) => renderValue(valueFor(v, dimension))).join(', ');

  if (!canSeeNames) {
    return <p className="text-xs text-slate-500">votes: {summary}</p>;
  }

  return (
    <div className="relative inline-block">
      <button
        type="button"
        aria-label="Show voters"
        onClick={() => setOpen((v) => !v)}
        className="text-xs text-slate-500 underline-offset-2 hover:underline"
      >
        votes: {summary}
      </button>
      {open && (
        <div className="absolute left-0 top-5 z-10 w-56 rounded border border-slate-200 bg-white p-2 text-xs shadow">
          <ul className="space-y-1">
            {[...present].sort((a, b) => a.voter.displayName.localeCompare(b.voter.displayName)).map((v) => (
              <li key={v.voter.id} className="flex justify-between gap-2">
                <span className={v.voter.id === LEGACY_VOTER_ID ? 'italic text-slate-500' : ''}>{v.voter.displayName}</span>
                <span className="font-medium">{renderValue(valueFor(v, dimension))}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
